import { RefObject, useEffect, useRef, useState } from 'react';

const useVerticalGrabScroll = (
  containerRef: RefObject<HTMLElement>,
  containerHeight: number,
  contentHeight: number,
  disabled = false
) => {
  const [isMouseDown, setIsMouseDown] = useState(false);
  const mouseScrollPosition = useRef<{ top: number; y: number }>({
    top: 0,
    y: 0,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    const container = containerRef.current;
    if (!container?.clientWidth) return;

    if (containerHeight >= contentHeight || disabled) {
      container.style.cursor = 'inherit';
      return;
    }

    if (isMouseDown) {
      container.style.cursor = 'grabbing';
      return;
    }

    container.style.cursor = 'grab';
  }, [containerHeight, contentHeight, isMouseDown, disabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    mouseScrollPosition.current = { top: 0, y: 0 };
    const container = containerRef.current;

    if (!container) return () => ({});
    if (disabled) {
      container.scrollTop = 0;

      return () => ({});
    }

    const mouseMoveHandler = (e: MouseEvent) => {
      const dy = e.clientY - mouseScrollPosition.current.y;

      container.scrollTop = mouseScrollPosition.current.top - dy;
    };

    const mouseUpHandler = () => {
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
      setIsMouseDown(false);
    };

    const mouseDownHandler = (e: MouseEvent) => {
      mouseScrollPosition.current = {
        top: container.scrollTop,
        y: e.clientY,
      };
      setIsMouseDown(true);

      document.addEventListener('mousemove', mouseMoveHandler);
      document.addEventListener('mouseup', mouseUpHandler);
    };

    container.addEventListener('mousedown', mouseDownHandler);

    return () => {
      container.removeEventListener('mousedown', mouseDownHandler);
    };
  }, [disabled]);
};

export default useVerticalGrabScroll;
